import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { X, Download, Share2, Smartphone } from "lucide-react";

export const PWAInstallPrompt = () => {
    const [showPrompt, setShowPrompt] = useState(false);
    const [isIOS, setIsIOS] = useState(false);
    const [isStandalone, setIsStandalone] = useState(false);

    useEffect(() => {
        // Check if already installed
        const standalone = window.matchMedia('(display-mode: standalone)').matches ||
                          window.navigator.standalone === true;
        setIsStandalone(standalone);

        // Check if iOS
        const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        setIsIOS(iOS);

        // Check if dismissed recently
        const dismissed = localStorage.getItem('pwa-prompt-dismissed');
        if (dismissed) {
            const dismissedDate = new Date(dismissed);
            const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
            if (daysSinceDismissed < 7) {
                return; // Don't show if dismissed within 7 days
            }
        }

        // Show prompt after delay (not on first visit)
        const hasVisited = localStorage.getItem('orkestria-visited');
        if (!standalone && hasVisited) {
            setTimeout(() => setShowPrompt(true), 3000);
        }
        localStorage.setItem('orkestria-visited', 'true');

        // Listen for PWA install ready event
        const handleInstallReady = () => {
            if (!standalone) {
                setShowPrompt(true);
            }
        };
        window.addEventListener('pwaInstallReady', handleInstallReady);

        return () => {
            window.removeEventListener('pwaInstallReady', handleInstallReady);
        };
    }, []);

    const handleInstall = async () => {
        if (window.installPWA) {
            await window.installPWA();
        }
        setShowPrompt(false);
    };

    const handleDismiss = () => {
        localStorage.setItem('pwa-prompt-dismissed', new Date().toISOString());
        setShowPrompt(false);
    };

    if (!showPrompt || isStandalone) {
        return null;
    }

    return (
        <div 
            className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:bottom-20 md:w-80 z-50 animate-in slide-in-from-bottom-5 duration-300"
            data-testid="pwa-install-prompt"
        >
            <div className="bg-card border border-border rounded-2xl shadow-xl p-4">
                <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                        <Smartphone className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-sm">Installer Orkestria</h3>
                        <p className="text-xs text-muted-foreground mt-1">
                            {isIOS 
                                ? "Appuyez sur Partager puis 'Sur l'écran d'accueil'"
                                : "Accédez rapidement à votre espace de travail"
                            }
                        </p>
                    </div>
                    <button
                        onClick={handleDismiss}
                        className="flex-shrink-0 p-1 hover:bg-muted rounded-full transition-colors"
                        aria-label="Fermer"
                    >
                        <X className="w-4 h-4 text-muted-foreground" />
                    </button>
                </div>
                
                <div className="flex gap-2 mt-4">
                    <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={handleDismiss}
                    >
                        Plus tard
                    </Button>
                    {isIOS ? (
                        <Button
                            size="sm"
                            className="flex-1 gap-2"
                            onClick={handleDismiss}
                        >
                            <Share2 className="w-4 h-4" />
                            Compris
                        </Button>
                    ) : (
                        <Button
                            size="sm"
                            className="flex-1 gap-2"
                            onClick={handleInstall}
                        >
                            <Download className="w-4 h-4" />
                            Installer
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PWAInstallPrompt;
